import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { User, CreateUserDto, UpdateUserDto, UserRole } from '../../types/user';
import { userService } from '../../services/userService';

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onSuccess: () => void;
}

const ROLES: { value: UserRole; label: string }[] = [
  { value: 'admin', label: 'Administrator' },
  { value: 'management', label: 'Management' },
  { value: 'bauleiter', label: 'Bauleiter' },
  { value: 'foreman', label: 'Vorarbeiter' },
  { value: 'installer', label: 'Monteur' },
];
console.log("🔍 DEBUG - ROLES array:", ROLES);

export const UserForm: React.FC<UserFormProps> = ({
  open,
  onClose,
  user,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(user);

  const { control, handleSubmit, formState: { errors }, reset, setValue } = useForm({
    defaultValues: {
      username: user?.username || '',
      employee_number: user?.employee_number || user?.employeeNumber || '',
      first_name: user?.first_name || user?.firstName || '',
      last_name: user?.last_name || user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      role: user?.role || 'installer',
      password: '',
      is_active: user?.is_active !== undefined ? user.is_active : (user?.isActive !== undefined ? user.isActive : true),
    },
  });

  // Reset form when user prop changes
  React.useEffect(() => {
    if (user) {
      reset({
        username: user.username || '',
        employee_number: user.employee_number || user.employeeNumber || '',
        first_name: user.first_name || user.firstName || '',
        last_name: user.last_name || user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'installer',
        password: '',
        is_active: user.is_active !== undefined ? user.is_active : (user.isActive !== undefined ? user.isActive : true),
      });
    }
  }, [user, reset]);

  // Fetch next employee number for new users
  React.useEffect(() => {
    if (!isEdit) {
      const fetchNextEmployeeNumber = async () => {
        try {
          // Get all users to calculate next number
          const users = await userService.getUsers();
          let maxNumber = 0;
          
          for (const u of users) {
            const empNum = u.employee_number || u.employeeNumber;
            if (empNum && typeof empNum === 'string' && empNum.match(/^\d+$/)) {
              maxNumber = Math.max(maxNumber, parseInt(empNum));
            }
          }
          
          const nextNumber = maxNumber + 1;
          const formattedNumber = nextNumber.toString().padStart(3, '0');
          setValue('employee_number', formattedNumber);
        } catch (error) {
          console.error('Error fetching next employee number:', error);
          setValue('employee_number', 'Wird automatisch generiert');
        }
      };
      
      fetchNextEmployeeNumber();
    }
  }, [isEdit, setValue]);

  const onSubmit = async (data: any) => {
    try {
      if (isEdit && user) {
        // For updates, exclude password if empty
        const updateData: UpdateUserDto = {
          username: data.username,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone: data.phone,
          role: data.role,
          is_active: data.is_active,
        };
        await userService.updateUser(user.id, updateData);
        enqueueSnackbar('Benutzer wurde erfolgreich aktualisiert', { variant: 'success' });
      } else {
        // For creation, all fields are required
        const createData: CreateUserDto = {
          username: data.username,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone: data.phone,
          role: data.role,
          password: data.password,
          is_active: data.is_active,
        };
        await userService.createUser(createData);
        enqueueSnackbar('Benutzer wurde erfolgreich erstellt', { variant: 'success' });
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || `Fehler beim ${isEdit ? 'Aktualisieren' : 'Erstellen'} des Benutzers`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="username"
                  control={control}
                  rules={{ required: 'Benutzername ist erforderlich' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Benutzername"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="employee_number"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Mitarbeiternummer"
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                      helperText={isEdit ? "Mitarbeiternummer kann nicht geändert werden" : "Wird automatisch generiert"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="first_name"
                  control={control}
                  rules={{ required: 'Vorname ist erforderlich' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Vorname"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="last_name"
                  control={control}
                  rules={{ required: 'Nachname ist erforderlich' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Nachname"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="email"
                  control={control}
                  rules={{ 
                    required: 'E-Mail ist erforderlich',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Ungültige E-Mail-Adresse'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="E-Mail"
                      type="email"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Telefon"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="role"
                  control={control}
                  rules={{ required: 'Rolle ist erforderlich' }}
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth error={!!fieldState.error}>
                      <InputLabel>Rolle</InputLabel>
                      <Select
                        {...field}
                        label="Rolle"
                      >
                        console.log("🔍 DEBUG - Mapping ROLES:", ROLES);
                        console.log("🔍 DEBUG - Mapping ROLES:", ROLES);
                        {ROLES.map((role) => (
                          <MenuItem key={role.value} value={role.value}>
                            {role.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldState.error && (
                        <Typography variant="caption" color="error">
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              {!isEdit && (
                <Grid item xs={12}>
                  <Controller
                    name="password"
                    control={control}
                    rules={{ 
                      required: 'Passwort ist erforderlich',
                      minLength: {
                        value: 8,
                        message: 'Passwort muss mindestens 8 Zeichen lang sein'
                      }
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Passwort"
                        type="password"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              )}
              {isEdit && (
                <Grid item xs={12}>
                  <Controller
                    name="is_active"
                    control={control}
                    render={({ field }) => (
                      <FormControl component="fieldset">
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Status
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip 
                            label={field.value ? 'Aktiv' : 'Inaktiv'} 
                            color={field.value ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ mr: 1 }}
                          />
                          <Button
                            size="small"
                            onClick={() => field.onChange(!field.value)}
                            variant="outlined"
                          >
                            {field.value ? 'Deaktivieren' : 'Aktivieren'}
                          </Button>
                        </Box>
                      </FormControl>
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="contained" color="primary">
            {isEdit ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}; 